// src/api/homeServiceApi.ts
import { AxiosResponse, AxiosError } from 'axios';
import apiClient from './apiClient';
import { areaCityCandidates, fetchCustomerCategories, fetchCustomerServices } from './customerCatalogApi';
import { Booking, Category, Service } from '../types/Login';

function isErrorEnvelope(data: unknown): boolean {
  if (!data || typeof data !== 'object') return false;
  const o = data as Record<string, unknown>;
  if (o.success === false) return true;
  if (typeof o.statusCode === 'number' && o.statusCode >= 400) return true;
  return false;
}

function parseArrayResponse<T>(data: unknown, keys: string[] = ['data', 'services', 'items']): T[] {
  if (Array.isArray(data)) return data as T[];
  if (!data || typeof data !== 'object') return [];
  const o = data as Record<string, unknown>;
  for (const key of keys) {
    const val = o[key];
    if (Array.isArray(val)) return val as T[];
    if (val && typeof val === 'object' && Array.isArray((val as Record<string, unknown>).data)) {
      return (val as { data: T[] }).data;
    }
  }
  const nested = o.data;
  if (nested && typeof nested === 'object') {
    for (const key of keys) {
      const val = (nested as Record<string, unknown>)[key];
      if (Array.isArray(val)) return val as T[];
    }
  }
  return [];
}

apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    console.error('API Error:', error);
    return Promise.reject(error);
  }
);

class HomeServiceApi {
  /** Customer: GET /services-catageory?area_city=… */
  async getCategories(areaCity?: string): Promise<Category[]> {
    try {
      return await fetchCustomerCategories(areaCity);
    } catch (error) {
      console.error('Error fetching categories:', error);
      return [];
    }
  }

  async getPopularServices(limit: number = 5, areaCity?: string): Promise<Service[]> {
    try {
      const candidates = areaCityCandidates(areaCity);
      const tries = candidates.length > 0 ? candidates : [undefined];
      for (const candidate of tries) {
        const params: Record<string, string | number> = { limit };
        if (candidate) params.area_city = candidate;
        const response = await apiClient.get('/services/popular', { params });
        if (isErrorEnvelope(response.data)) continue;
        const raw = (response.data as Record<string, unknown>)?.data ?? response.data;
        const arr = parseArrayResponse<Service>(raw, ['data', 'services', 'items']);
        if (Array.isArray(arr) && arr.length > 0) return arr;
      }
      return [];
    } catch (error) {
      console.error('Error fetching popular services:', error);
      return [];
    }
  }

  /** Customer: GET /services?area_city=… */
  async getAllServices(areaCity?: string): Promise<Service[]> {
    try {
      return await fetchCustomerServices(areaCity);
    } catch (error) {
      console.error('Error fetching all services:', error);
      return [];
    }
  }

  async getUserBookings(_userId: string): Promise<Booking[]> {
    try {
      const response = await apiClient.get(`/order-management`, { params: { limit: 50 } });
      return parseArrayResponse<Booking>(response.data, ['data', 'bookings', 'orders', 'items']);
    } catch (error) {
      console.error('Error fetching user bookings:', error);
      return [];
    }
  }

  async bookService(userId: string, serviceId: string, date: Date): Promise<Booking> {
    try {
      const response = await apiClient.post(`/users/${userId}/bookings`, {
        serviceId,
        date,
      });
      return response.data;
    } catch (error) {
      console.error('Error booking service:', error);
      throw error;
    }
  }

  async searchServices(serviceName: string, areaCity?: string): Promise<Service[]> {
    try {
      const params: Record<string, string> = { serviceName: (serviceName || '').trim() };
      if (areaCity?.trim()) params.area_city = areaCity.trim();
      const response = await apiClient.get('/services/searchServices', { params });
      const data = (response.data as Record<string, unknown>)?.data ?? response.data;
      const d = data as Record<string, unknown>;
      const services = d?.services ?? [];
      const serviceCatageory = d?.serviceCatageory ?? [];
      return [
        ...(Array.isArray(services) ? services : []),
        ...(Array.isArray(serviceCatageory) ? serviceCatageory : []),
      ] as Service[];
    } catch (error) {
      console.error('Error searching services:', error);
      throw error;
    }
  }

  async getServiceById(serviceId: string): Promise<Service | null> {
    try {
      const response = await apiClient.get(`/services/${serviceId}`);
      const body = response.data as Record<string, unknown>;
      return (body?.data ?? response.data ?? null) as Service | null;
    } catch (error) {
      console.error('Error fetching service by ID:', error);
      throw error;
    }
  }

  async cancelBooking(userId: string, bookingId: string, reason?: string): Promise<void> {
    const { cancelOrder } = await import('./orderApi');
    await cancelOrder(bookingId, { orderUserId: userId, reason });
  }
}

export const homeServiceApi = new HomeServiceApi();
